import thdCustomer from '@thd-olt-global/thd-customer';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_PREQUAL_URL, DEFAULT_APPLYNOW_URL, B2B_DEFAULT_APPLYNOW_URL } from './constants';

const getScriptInHead = (head, url) => {
  const scripts = head.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i += 1) {
    if (scripts[i].src === url) return scripts[i];
  }
  return false;
};

export const loadEstimatorScript = (fileVersion, onLoadFunction, isMobile) => {
  function paymentEstimatorExists() {
    return Boolean(window.THD?.PaymentEstimator);
  }

  function createScript(fileUrl, head) {
    const script = document.createElement('script');
    script.setAttribute('src', fileUrl);
    script.onload = () => {
      onLoadFunction(isMobile);
    };
    head.appendChild(script);
  }

  function updateExistingScript(head, fileUrl) {
    const script = getScriptInHead(head, fileUrl);
    if (paymentEstimatorExists()) {
      onLoadFunction(isMobile);
    } else {
      let previousOnLoad = script.onload;
      script.onload = () => {
        previousOnLoad();
        onLoadFunction(isMobile);
      };
    }
  }

  if (paymentEstimatorExists()) {
    onLoadFunction(isMobile);
  } else {
    const location = window ? window.location.host.toLowerCase() : '';
    const isLlcEnvironment = !location.includes('www.homedepot.com');
    const fileUrl = isLlcEnvironment
      ? `https://assets-qa.homedepot-static.com/paymentestimator/${fileVersion}/payment-estimator.js`
      : `https://assets.homedepot-static.com/paymentestimator/${fileVersion}/payment-estimator.js`;
    const head = document.querySelector('head');
    if (getScriptInHead(head, fileUrl)) {
      updateExistingScript(head, fileUrl);
    } else {
      createScript(fileUrl, head);
    }
  }
};

const introOfferDetails = (estimator, isMobile, pageName, isB2B) => {
  const { defaultApplynowLinks, defaultB2BApplynowLinks } = estimator || {};
  let links = null;
  if (isB2B) {
    links = defaultB2BApplynowLinks ? defaultB2BApplynowLinks(pageName) : null;
  } else {
    links = defaultApplynowLinks ? defaultApplynowLinks(pageName) : null;
  }
  if (!links) {
    return isB2B ? B2B_DEFAULT_APPLYNOW_URL : DEFAULT_APPLYNOW_URL;
  }
  return { applyNowUrl: isMobile ? links.mobileUrl : links.desktopUrl };
};

const prequalOfferDetails = (estimator, isMobile, pageName, invitationNumber) => {
  const { prequalApplyNowLinks } = estimator || {};
  const links = prequalApplyNowLinks ? prequalApplyNowLinks(pageName, invitationNumber) : null;
  if (!links) {
    return { prequalUrl: DEFAULT_PREQUAL_URL };
  }
  return { prequalUrl: isMobile ? links.mobileUrl : links.desktopUrl };
};

export const getIntroOfferLink = (isMobile, pageName = 'pip', isB2B = false) => {
  const estimator = window && window.THD ? (window.THD || {}).PaymentEstimator : null;
  if (!estimator) {
    return false;
  }
  return introOfferDetails(estimator, isMobile, pageName, isB2B).applyNowUrl;
};

export const getPrequalOfferLink = (isMobile, pageName, invitationNumber) => {
  const estimator = window && window.THD ? (window.THD || {}).PaymentEstimator : null;
  if (!estimator) {
    return false;
  }
  return prequalOfferDetails(estimator, isMobile, pageName, invitationNumber).prequalUrl;
};

export const getPaymentEstimatorDetails = (price, isMobile, isAppliance, isHDHome, pageName = 'pip') => {
  const estimator = window && window.THD ? (window.THD || {}).PaymentEstimator : null;
  if (!estimator) {
    return false;
  }
  if (price < 299) {
    return introOfferDetails(estimator, isMobile, pageName);
  }
  const { currentPromotionDates, estimatedPayments, paymentEstimatorApplynowLinks, toolTipText } = estimator;
  const { monthlyPayment, promotionTerm } = estimatedPayments ? estimatedPayments(price, isAppliance, isHDHome) : {};
  const { promoStartDate, promoEndDate } = currentPromotionDates
    ? currentPromotionDates(isAppliance, isHDHome, price) : {};
  const { mobileUrl, desktopUrl } = paymentEstimatorApplynowLinks
    ? paymentEstimatorApplynowLinks(pageName, isAppliance, isHDHome, price) : {};
  const tooltipText = toolTipText ? toolTipText() : null;
  let details = {};

  if (window.THD?.PaymentEstimator) {
    details = {
      displayPrice: monthlyPayment,
      promotionTerm,
      applyNowUrl: isMobile ? mobileUrl : desktopUrl,
      activeStartDate: promoStartDate,
      activeEndDate: promoEndDate,
      tooltipText
    };
  }
  return details;
};

export const isNotAuthenticated = () => {
  return (
    (thdCustomer.isKMSI && thdCustomer.isPersisted)
    || !thdCustomer.isLoggedIn
  );
};

export const getUserName = () => {
  return thdCustomer?.username || null;
};

const plccMaskSensitiveFields = (data) => {
  let maskedData = JSON.parse(JSON.stringify(data));
  if (maskedData.accessToken) {
    maskedData.accessToken = maskedData.accessToken.slice(0, 5) + '######';
  }
  if (maskedData.paymentCards) {
    maskedData.paymentCards = '#####';
  }
  return maskedData;
};

export const newRelic = {
  logPlccApiCall(url) {
    if (
      typeof window.newrelic !== 'undefined'
      && typeof window.newrelic.addPageAction === 'function'
      && url
    ) {
      window.newrelic.addPageAction('PLCC API Call', { plccApiCallUrl: url });
    }
  },
  logPlccApiResponse(data, status, apiCallDurationInSeconds) {
    if (
      typeof window.newrelic !== 'undefined'
      && typeof window.newrelic.addPageAction === 'function'
      && data
    ) {
      const maskedData = plccMaskSensitiveFields(data);
      window.newrelic.addPageAction('PLCC API Response', maskedData);
      if (status) {
        window.newrelic.addPageAction('PLCC API Response Code', {
          plccApiResponseCode: status,
        });
      }
      if (apiCallDurationInSeconds) {
        window.newrelic.addPageAction('PLCC API Call Duration', {
          plccApiCallDuration: apiCallDurationInSeconds,
        });
      }
    }
  }
};

function generateCustAcctHeaders(isMobile) {
  return {
    accept: 'application/json',
    'Content-type': 'application/json',
    channelId: isMobile ? '2' : '1',
    Authorization: window.encodeURIComponent(thdCustomer.token),
    TMXProfileID: window?.THD_ACCOUNT_LIB?.TMX ? window.THD_ACCOUNT_LIB.TMX.getTMXsessionId() : ''
  };
}

const generateFetchHeaders = (isMobile, requestBody) => {
  let body = JSON.stringify(requestBody) || null;
  let method = requestBody ? 'POST' : 'GET';

  return {
    headers: {
      ...generateCustAcctHeaders(isMobile),
      'cache-control': 'no-cache',
      'cust-acct-client-delay-token-validation': '1000000',
      'x-trace-uuid': uuidv4()
    },
    body,
    method,
    credentials: 'include'
  };
};

export const fetchPlccData = (channel, storeId) => {
  const generateFetchUrl = () => {
    return `${window.location.origin}/customer/account/v1/${thdCustomer.svocID}/sso/account-snapshot`;
  };

  const channelType = (channel === 'mobile') ? 'mobileweb' : 'desktop';

  const requestBody = {
    SnapshotRequest: {
      source: 'PHP',
      storeId,
      deviceType: channelType
    }
  };

  const isMobile = channel === 'mobile';

  return fetch(generateFetchUrl(), generateFetchHeaders(isMobile, requestBody));
};

export const getSsoUrl = (isMobile, siteId) => {
  const generateFetchUrl = () => {
    return `${window.location.origin}/customer/account/v1/${thdCustomer.svocID}/sso/getURL?siteId=${siteId}`;
  };

  return fetch(generateFetchUrl(), generateFetchHeaders(isMobile));
};

export const fetchDeclinePrequalOffer = (invitationNo, isMobile) => {
  const generateFetchUrl = () => {
    return `${window.location.origin}/customer/account/v1/citi/${thdCustomer.svocID}/prequalDeclineOffer`;
  };

  const requestBody = {
    PrequalDeclineRequest: {
      userId: thdCustomer.userID,
      svocId: thdCustomer.svocID,
      invitationNo,
    }
  };

  newRelic.logPlccApiCall(generateFetchUrl());
  return fetch(generateFetchUrl(), generateFetchHeaders(isMobile, requestBody));
};
